import { useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { TooltipTrigger } from '@radix-ui/react-tooltip'
import { IconInfoCircle, IconPlus, IconX } from '@tabler/icons-react'
import { useUploadStudyTextFiles } from '@/services/studyTextService'
import { toast } from 'sonner'
import importImg from '@/assets/svg/upload_file.svg'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip'

interface Props {
  moduleId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  refreshStudyTexts: () => void
}

const ImportFilesModal = ({
  open,
  moduleId,
  onOpenChange,
  refreshStudyTexts,
}: Props) => {
  const [files, setFiles] = useState<File[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { uploadFilesAsync, isUploading } = useUploadStudyTextFiles()

  const isAllowedDocument = (file: File) => {
    const name = file.name.toLowerCase()
    const type = file.type
    const isPdf = name.endsWith('.pdf') || type === 'application/pdf'
    const isDoc = name.endsWith('.doc') || type === 'application/msword'
    const isDocx =
      name.endsWith('.docx') ||
      type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    return isPdf || isDoc || isDocx
  }

  const clearSelectedFiles = () => {
    setFiles([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      const validFiles: File[] = []

      droppedFiles.forEach((file) => {
        if (isAllowedDocument(file)) {
          validFiles.push(file)
        }
      })

      if (validFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...validFiles])
        if (validFiles.length < droppedFiles.length) {
          toast.error(
            'Some files were skipped. Only PDF or Word files are allowed.'
          )
        }
      } else {
        toast.error('Please upload only PDF or Word files (.pdf, .doc, .docx)')
      }
    }
  }

  const calculateTotalFileSize = (files: File[]) => {
    return files.reduce((total, file) => total + file.size, 0)
  }

  const onSubmit = async () => {
    if (files.length === 0) return

    const totalSize = calculateTotalFileSize(files)
    const maxSize = 10 * 1024 * 1024 // 10MB in bytes

    if (totalSize > maxSize) {
      toast.error(
        'Total file size exceeds 10MB. Please select smaller files.',
        {
          duration: 3000,
          position: 'top-right',
        }
      )
      return
    }

    uploadFilesAsync({ moduleId: Number(moduleId), files })
      .then(() => {
        toast.success('Upload study text successfully', {
          duration: 3000,
          position: 'top-right',
        })
        refreshStudyTexts()
        onOpenChange(false)
      })
      .catch((error: AxiosError) => {
        clearSelectedFiles()
        const errorMsg =
          typeof (error.response?.data as { detail?: string })?.detail ===
          'string'
            ? (error.response?.data as { detail?: string })?.detail
            : 'Failed to upload file'

        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
      })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        onOpenChange(val)
        if (!val) {
          clearSelectedFiles()
        }
      }}
    >
      <DialogContent className='gap-2 sm:max-w-sm'>
        <DialogHeader className='text-left'>
          <DialogTitle>Import study text</DialogTitle>
        </DialogHeader>

        <div className='relative'>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <IconInfoCircle className='absolute top-5 left-2 z-50 min-h-6 min-w-6' />
              </TooltipTrigger>
              <TooltipContent className='max-w-[300px]'>
                <p>
                  Uploaded study texts will be used by AI during the marking
                  process. Please note that the total file size for all files
                  uploaded at once should not exceed 10 MB. Once uploaded
                  successfully, these study texts cannot be deleted.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div
            className={`relative mt-3 mb-2 flex min-h-[163px] cursor-pointer flex-col items-center justify-center gap-3 space-y-1 rounded-[12px] border p-6 transition-colors ${
              isDragOver
                ? 'border-[#5741C6] bg-[#5741C6]/5'
                : 'border-[#EAECF0]'
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <img src={importImg} alt='upload img'></img>
            <Input
              type='file'
              accept='.pdf,.doc,.docx'
              multiple
              ref={fileInputRef}
              onClick={(e) => {
                ;(e.currentTarget as HTMLInputElement).value = ''
              }}
              onChange={(e) => {
                const selectedFiles = Array.from(e.target.files || [])
                const allowed = selectedFiles.filter(isAllowedDocument)
                if (allowed.length > 0) {
                  setFiles((prevFiles) => [...prevFiles, ...allowed])
                }
                if (allowed.length < selectedFiles.length) {
                  toast.error(
                    'Some files were skipped. Only PDF or Word files are allowed.'
                  )
                }
              }}
              className='hidden'
            />

            <div className='text-center text-sm'>
              {isDragOver ? (
                <>
                  <b className='text-[#5741C6]'>Drop file here</b> to upload
                </>
              ) : (
                <>
                  <b className='text-[#5741C6]'>Click to upload</b> or drag and
                  drop Docs or Pdf.
                </>
              )}
            </div>
          </div>

          <div className='max-w-[334px] rounded-md border border-[#EAECF0] px-3 py-3'>
            <p className='text-sm'>Files ({files.length})</p>
            <div className='mt-1 flex max-h-[200px] w-full flex-col gap-2 overflow-auto'>
              {files.length === 0 ? (
                <p className='text-sm text-gray-500 italic'>
                  No files selected
                </p>
              ) : (
                files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className='flex items-center justify-between gap-1 rounded-sm border border-[#EAECF0] px-2 py-1'
                  >
                    <p
                      className='truncate text-[13px] font-bold'
                      title={file.name}
                    >
                      {file.name}
                    </p>
                    <button
                      className='rounded-sm p-1 hover:bg-[#ffefef]'
                      onClick={() => removeFile(index)}
                      type='button'
                    >
                      <IconX />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter className='mt-3 gap-2'>
          <DialogClose asChild>
            <Button
              onClick={() => {
                clearSelectedFiles()
              }}
              variant='outline'
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            className='w-[94px]'
            onClick={onSubmit}
            disabled={files.length === 0 || isUploading}
          >
            {isUploading ? (
              <div className='loader' />
            ) : (
              <>
                <IconPlus />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ImportFilesModal
